/**
 * V70-H — 게임대회 화면 조회 캐시 (브라우저 메모리, 새로고침하면 비워짐)
 *
 * 목적: 탭 이동·재진입마다 같은 Callable 을 다시 기다리며 화면 전체가 "로딩 중…" 으로 바뀌던 지연 제거.
 *   - 같은 키 동시 요청은 1회로 합침 (중복 호출 방지)
 *   - 이미 받은 데이터가 있으면 즉시 표시, 30초가 지났으면 화면은 유지한 채 뒤에서 다시 조회
 *   - 강제 새로고침(신청·취소·공지 저장/삭제 후)은 진행 중 요청과 무관하게 새로 조회
 *   - 가장 마지막에 시작한 요청 결과만 캐시에 기록 → 늦게 도착한 이전 응답이 최신 화면을 덮어쓰지 않음
 *   - 키에 사용자 UID 를 포함해 다른 사용자 데이터가 섞이지 않게 한다 (호출부 책임)
 * 서버 권한 검사·정렬·필터는 그대로이며 화면 표시 시점만 바뀐다.
 */
import { useCallback, useEffect, useReducer, useRef, useState } from 'react';

export const TOURNAMENT_QUERY_FRESH_MS = 30_000;

interface Entry { data: unknown; at: number }

const store = new Map<string, Entry>();
const inflight = new Map<string, { seq: number; promise: Promise<unknown> }>();
const listeners = new Map<string, Set<() => void>>();
let seqCounter = 0;

function notify(key: string): void {
  listeners.get(key)?.forEach((fn) => fn());
}

export function subscribeQuery(key: string, fn: () => void): () => void {
  let set = listeners.get(key);
  if (!set) { set = new Set(); listeners.set(key, set); }
  set.add(fn);
  return () => { set!.delete(fn); };
}

export function peekQuery<T>(key: string): { data: T; at: number } | undefined {
  return store.get(key) as { data: T; at: number } | undefined;
}

export function isQueryFresh(key: string, freshMs: number = TOURNAMENT_QUERY_FRESH_MS): boolean {
  const e = store.get(key);
  return !!e && Date.now() - e.at < freshMs;
}

/** 캐시에 직접 기록 (예: 목록에서 이어서 불러온 결과 반영) */
export function setQueryData<T>(key: string, data: T): void {
  store.set(key, { data, at: Date.now() });
  notify(key);
}

/** 조회 1회. 같은 키가 진행 중이면 그 요청을 공유하고, force 면 새 요청을 시작한다. */
export function fetchQuery<T>(key: string, fetcher: () => Promise<T>, opts: { force?: boolean } = {}): Promise<T> {
  const cur = inflight.get(key);
  if (cur && !opts.force) return cur.promise as Promise<T>;
  const seq = ++seqCounter;
  const promise = fetcher().then(
    (data) => {
      if (inflight.get(key)?.seq === seq) {
        inflight.delete(key);
        store.set(key, { data, at: Date.now() });
        notify(key);
      }
      return data;
    },
    (err) => {
      if (inflight.get(key)?.seq === seq) inflight.delete(key);
      throw err;
    },
  );
  inflight.set(key, { seq, promise });
  return promise;
}

/** 캐시 무효화 — 정확히 같은 키 또는 접두사(prefix: true) */
export function invalidateQuery(key: string, opts: { prefix?: boolean } = {}): void {
  const keys = opts.prefix ? Array.from(store.keys()).filter((k) => k.startsWith(key)) : [key];
  keys.forEach((k) => store.delete(k));
}

/** 테스트 격리용 — 모든 캐시·진행 중 요청 초기화 */
export function resetTournamentQueryCache(): void {
  store.clear();
  inflight.clear();
}

export interface TournamentQueryState<T> {
  /** 표시할 데이터 (캐시 또는 최신 응답). 아직 한 번도 받지 못했으면 undefined */
  data: T | undefined;
  /** 표시할 데이터가 아직 없고 조회 중 (최초 진입) */
  loading: boolean;
  /** 데이터를 표시한 채 뒤에서 다시 조회 중 */
  refreshing: boolean;
  /** 가장 최근 요청의 오류 (성공하면 null) */
  error: unknown;
  /** 강제 새로고침 — 표시 중인 데이터는 유지 */
  reload: () => Promise<void>;
}

/**
 * key 가 null 이면 조회하지 않는다 (예: 사용자 정보 로딩 전).
 * freshMs 이내 재진입은 호출 없음, 지난 경우 캐시를 먼저 보여 주고 뒤에서 다시 조회.
 */
export function useTournamentQuery<T>(
  key: string | null,
  fetcher: () => Promise<T>,
  opts: { freshMs?: number } = {},
): TournamentQueryState<T> {
  const freshMs = opts.freshMs ?? TOURNAMENT_QUERY_FRESH_MS;
  const [, rerender] = useReducer((x: number) => x + 1, 0);
  const [error, setError] = useState<unknown>(null);
  const [pending, setPending] = useState(false);
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;
  const keyRef = useRef(key);
  keyRef.current = key;
  const runSeq = useRef(0);
  const mounted = useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);

  const run = useCallback(async (force: boolean): Promise<void> => {
    const k = keyRef.current;
    if (!k) return;
    const my = ++runSeq.current;
    setPending(true);
    try {
      await fetchQuery(k, () => fetcherRef.current(), { force });
      if (mounted.current && runSeq.current === my) { setError(null); setPending(false); }
    } catch (e) {
      if (mounted.current && runSeq.current === my) { setError(e); setPending(false); }
    }
  }, []);

  useEffect(() => {
    if (!key) return undefined;
    return subscribeQuery(key, rerender);
  }, [key]);

  useEffect(() => {
    if (!key) return;
    setError(null);
    if (!isQueryFresh(key, freshMs)) void run(false);
  }, [key, freshMs, run]);

  const entry = key ? store.get(key) : undefined;
  const reload = useCallback(() => run(true), [run]);
  return {
    data: entry?.data as T | undefined,
    loading: !!key && !entry && !error,
    refreshing: pending && !!entry,
    error,
    reload,
  };
}
